import CollectionVariantBuyStack, {
  CollectionVariantData,
} from '@/components/collection/CollectionVariantBuyStack';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {logError, logInfo} from '@/utilities/log';
import {preloadImage} from '@/utilities/preload-image';
import register from 'preact-custom-element';
import {z} from 'zod';

/**
 * Schema for validating variant data passed from liquid
 */
const collectionVariantDataSchema = z.object({
  id: z.string(),
  productUrl: z.string(),
  displayTitle: z.string(),
  displayVarTitle: z.string(),
  available: z.boolean(),
  image: z
    .object({
      url: z.string(),
      alt: z.string(),
    })
    .nullable(),
  price: z.number(),
  compareAtPrice: z.number().nullable(),
  hasSubscription: z.boolean(),
  subscriptionPrice: z.number().nullable(),
  subscriptionCompareAtPrice: z.number().nullable(),
  selectedSellingPlanId: z.string().nullable(),
  tierDiscountedPrice: z.number().nullable(),
  sellingPlanAllocations: z.array(
    z.object({
      price: z.number(),
      compareAtPrice: z.number(),
      sellingPlan: z.object({
        id: z.string(),
        name: z.string(),
        recurringDeliveries: z.boolean(),
      }),
    })
  ),
});

/**
 * Props for the CollectionVariantBuyStackLoader component
 */
type CollectionVariantBuyStackLoaderProps = {
  /**
   * The JSON stringified variant data, passed in via liquid
   */
  'variant-data': string;
};

/**
 * Custom element that renders the CollectionVariantBuyStack component
 */
function CollectionVariantBuyStackLoader({
  'variant-data': variantDataJson,
}: CollectionVariantBuyStackLoaderProps) {
  const variantData = loadVariantData(variantDataJson);
  if (!variantData) return <></>;

  return <CollectionVariantBuyStack variantData={variantData} />;
}

function loadVariantData(variantDataJson: string): CollectionVariantData | null {
  try {
    const variantData = collectionVariantDataSchema.parse(
      JSON.parse(variantDataJson)
    );

    // Preload the variant image for smoother transitions
    if (variantData.image) {
      preloadImage(createShopifyImageUrl(variantData.image.url, 440));
    }

    if (import.meta.env.DEV) {
      logInfo('CollectionVariantLoaded', {variantDataJson, variantData});
    }

    return variantData;
  } catch (error) {
    logError(error, {
      message: 'ErrorLoadingVariantData',
      variantDataJson,
    });
    return null;
  }
}

register(CollectionVariantBuyStackLoader, 'collection-variant-buy-stack', [
  'variant-data',
]);
